import React from 'react';
import GoogleMapReact from 'google-map-react';
import { cn } from '@/lib/utils';

// Marker components: a dot with a white ring, and the name in a chip beneath it.
interface MarkerProps {
  lat: number;
  lng: number;
  name?: string;
  onClick?: () => void;
  isSaved?: boolean;
  isHovered?: boolean;
  isSelected?: boolean;
}

const labelChip =
  "mt-1.5 whitespace-nowrap rounded-[6px] border border-foreground bg-background px-2 py-0.5 text-[12px] font-medium leading-[1.4] text-foreground shadow-[0_1px_3px_rgba(0,0,0,0.35)]";

/** A place picked from search that is not saved yet: black dot. */
const RestaurantMarker: React.FC<MarkerProps> = ({ name, onClick }) => (
  <div
    className="absolute flex -translate-x-1/2 -translate-y-[7px] cursor-pointer flex-col items-center"
    onClick={onClick}
    title={name}
  >
    <div className="size-3.5 rounded-full border-2 border-white bg-foreground shadow-[0_1px_3px_rgba(0,0,0,0.4)]" />
    {name && <div className={labelChip}>{name}</div>}
  </div>
);

/** A saved restaurant: red dot; grows on hover, and on selection the chip turns red. */
const SavedRestaurantMarker: React.FC<MarkerProps> = ({ name, onClick, isHovered = false, isSelected = false }) => (
  <div
    className={cn(
      "absolute flex -translate-x-1/2 -translate-y-[7px] cursor-pointer flex-col items-center",
      (isHovered || isSelected) && "z-10"
    )}
    onClick={onClick}
    title={name}
  >
    <div
      className={cn(
        "rounded-full border-2 border-white bg-brand shadow-[0_1px_3px_rgba(0,0,0,0.4)] transition-[width,height,outline]",
        isSelected ? "size-[18px] outline outline-2 outline-offset-2 outline-white" : isHovered ? "size-4" : "size-3.5"
      )}
    />
    {name && (
      <div className={cn(labelChip, isSelected && "border-brand bg-brand text-primary-foreground")}>{name}</div>
    )}
  </div>
);

const LocationMarker: React.FC<MarkerProps> = ({ name }) => (
  <div
    className="relative transform -translate-x-1/2 -translate-y-1/2"
    title={name}
  >
    <div className="h-4 w-4 bg-primary rounded-full animate-pulse"></div>
    {name && (
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-background border rounded-md px-2 py-1 text-xs whitespace-nowrap">
        {name}
      </div>
    )}
  </div>
);

// Restaurant data interface
interface Restaurant {
  place_id: string;
  name: string;
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  rating?: number;
  price_level?: number;
  types: string[];
  photos?: any[];
}

// Saved restaurant interface (from backend)
interface SavedRestaurant {
  id: string;
  placeId: string;
  name: string;
  formattedAddress: string;
  latitude: string;
  longitude: string;
  rating?: string | null;
  priceLevel?: number | null;
  types: any;
  photos?: any[] | null;
}

interface RestaurantMapProps {
  height?: string;
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
  selectedPlace?: Restaurant | null;
  savedRestaurants?: SavedRestaurant[];
  onGoogleApiLoaded?: (maps: { map: any; maps: any; ref: Element | null }) => void;
  onSavedRestaurantClick?: (restaurant: SavedRestaurant) => void;
  hoveredId?: string | null;
  selectedId?: string | null;
}

export const RestaurantMap: React.FC<RestaurantMapProps> = ({
  height = "500px",
  center = { lat: 45.5152, lng: -122.6784 }, // Portland, OR default
  zoom = 13,
  selectedPlace,
  savedRestaurants = [],
  onGoogleApiLoaded,
  onSavedRestaurantClick,
  hoveredId = null,
  selectedId = null
}) => {

  // Adjust zoom based on selected place
  const mapZoom = selectedPlace ? 15 : zoom;
  const mapCenter = center || { lat: 45.5152, lng: -122.6784 };

  const mapOptions = {
    styles: [
      // Dark theme map style for retro aesthetic
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#8ec3b9"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1a3646"
          }
        ]
      },
      {
        "featureType": "administrative.country",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#4b6878"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#64779f"
          }
        ]
      },
      {
        "featureType": "administrative.province",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#4b6878"
          }
        ]
      },
      {
        "featureType": "landscape.man_made",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#334e87"
          }
        ]
      },
      {
        "featureType": "landscape.natural",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#023e58"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#283d6a"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#6f9ba4"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry.fill",
        "stylers": [
          {
            "color": "#023e58"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#3C7680"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#304a7d"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#98a5be"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#2c6675"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#255763"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#b0d5ce"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#023e58"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#98a5be"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry.fill",
        "stylers": [
          {
            "color": "#283d6a"
          }
        ]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#3a4762"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#0e1626"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#4e6d70"
          }
        ]
      }
    ]
  };

  return (
    <div
      className="rounded-lg border shadow-sm overflow-hidden"
      style={{ height }}
    >
      <GoogleMapReact
        bootstrapURLKeys={{
          key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
          libraries: ['places', 'geometry']
        }}
        center={mapCenter}
        zoom={mapZoom}
        options={mapOptions}
        onGoogleApiLoaded={onGoogleApiLoaded}
        yesIWantToUseGoogleMapApiInternals
      >
        {/* Saved restaurant markers */}
        {savedRestaurants.map((restaurant) => (
          <SavedRestaurantMarker
            key={restaurant.id}
            lat={parseFloat(restaurant.latitude)}
            lng={parseFloat(restaurant.longitude)}
            name={restaurant.name}
            onClick={() => onSavedRestaurantClick?.(restaurant)}
            isHovered={hoveredId === restaurant.id}
            isSelected={selectedId === restaurant.id}
          />
        ))}

        {/* Single marker for selected place */}
        {selectedPlace && (
          selectedPlace.place_id === 'current-location' ? (
            <LocationMarker
              key={selectedPlace.place_id}
              lat={selectedPlace.geometry.location.lat}
              lng={selectedPlace.geometry.location.lng}
              name="Your Location"
            />
          ) : (
            <RestaurantMarker
              key={selectedPlace.place_id}
              lat={selectedPlace.geometry.location.lat}
              lng={selectedPlace.geometry.location.lng}
              name={selectedPlace.name}
            />
          )
        )}
      </GoogleMapReact>
    </div>
  );
};

export default RestaurantMap;